import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { usePanelTokens } from './tokens';

export interface RingGaugeProps {
  /** 0–100. */
  value: number;
  size?: number;
  stroke?: number;
  color?: string;
  trackColor?: string;
  centerTop?: ReactNode;
  centerBottom?: ReactNode;
  /** Optional second segment drawn after ``value`` up to this total (0–100), e.g. committed
   *  spend after spent. Rendered in ``secondaryColor`` (default: a lighter accent). */
  secondaryValue?: number;
  secondaryColor?: string;
  /** Optional hand (0–100) marking a position on the ring, e.g. how much of the year is gone. */
  hand?: number;
  handColor?: string;
  /** Accessible description; the ring is exposed as role="img" when given. */
  ariaLabel?: string;
}

/** A single-value donut ring with optional center label (pure SVG, crisp). */
export function RingGauge({
  value,
  size = 112,
  stroke = 11,
  color,
  trackColor,
  centerTop,
  centerBottom,
  secondaryValue,
  secondaryColor,
  hand,
  handColor,
  ariaLabel,
}: RingGaugeProps) {
  const t = usePanelTokens();
  const arc = color ?? t.a;
  const track = trackColor ?? t.aSoft;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  const offset = c * (1 - pct / 100);
  const total = secondaryValue == null ? pct : Math.max(pct, Math.min(100, secondaryValue));
  const handPct = hand == null ? null : Math.max(0, Math.min(100, hand));
  const angle = handPct == null ? 0 : (handPct / 100) * 2 * Math.PI - Math.PI / 2;
  return (
    <Box
      sx={{ position: 'relative', width: size, height: size, flex: 'none' }}
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden={ariaLabel ? true : undefined}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
        {total > pct && (
          <circle
            data-testid="ring-secondary"
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={secondaryColor ?? t.a3}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={c}
            strokeDashoffset={c * (1 - total / 100)}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: 'stroke-dashoffset 0.6s ease' }}
          />
        )}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={arc}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
        {handPct != null && (
          <line
            data-testid="ring-hand"
            x1={size / 2 + (r - stroke) * Math.cos(angle)}
            y1={size / 2 + (r - stroke) * Math.sin(angle)}
            x2={size / 2 + (r + stroke / 2) * Math.cos(angle)}
            y2={size / 2 + (r + stroke / 2) * Math.sin(angle)}
            stroke={handColor ?? t.ink}
            strokeWidth={2}
            strokeLinecap="round"
          />
        )}
      </svg>
      {(centerTop != null || centerBottom != null) && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {centerTop != null && (
            <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: t.ink, lineHeight: 1 }}>
              {centerTop}
            </Typography>
          )}
          {centerBottom != null && (
            <Typography sx={{ fontSize: '0.625rem', color: t.label, mt: 0.4 }}>
              {centerBottom}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

export interface ConcentricRingsProps {
  /** Outer → inner. Each: value 0–100 + color. */
  rings: { value: number; color: string }[];
  size?: number;
  stroke?: number;
  gap?: number;
}

/** Nested tonal rings — the "resource usage" widget. */
export function ConcentricRings({ rings, size = 156, stroke = 9, gap = 5 }: ConcentricRingsProps) {
  const t = usePanelTokens();
  const cx = size / 2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flex: 'none' }}>
      {rings.map((ring, i) => {
        const r = (size - stroke) / 2 - i * (stroke + gap);
        if (r <= 0) return null;
        const c = 2 * Math.PI * r;
        const pct = Math.max(0, Math.min(100, ring.value));
        return (
          <g key={i}>
            <circle cx={cx} cy={cx} r={r} fill="none" stroke={t.aSoft} strokeWidth={stroke} />
            <circle
              cx={cx}
              cy={cx}
              r={r}
              fill="none"
              stroke={ring.color}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={c}
              strokeDashoffset={c * (1 - pct / 100)}
              transform={`rotate(-90 ${cx} ${cx})`}
              style={{ transition: 'stroke-dashoffset 0.6s ease' }}
            />
          </g>
        );
      })}
    </svg>
  );
}

/** Small colored legend dot + label row. */
export function LegendDot({ color, children }: { color: string; children: ReactNode }) {
  const t = usePanelTokens();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.78rem', color: t.label }}>
      <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: color, flex: 'none' }} />
      {children}
    </Box>
  );
}
